/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './ViewAttachment';

const ViewAttachmentManager: ComponentManagerFn<'ViewAttachment'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'ViewAttachment'>(platform, 'ViewAttachment'),
    type: 'ViewAttachment',
    name: 'View Attachment',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      attachmentId: {
        label: 'Attachment Id',
        type: 'expression',
        returnType: async () => 'string',
      },
      attachmentVersion: {
        label: 'Version Id',
        type: 'expression',
        returnType: async () => 'string',
      },
      attachmentHeight: {
        label: 'Height',
        type: 'number',
        min: 0,
        max: 800,
        step: 40,
        helpText:
          'Specify the height. set zero value to take up the parent height.',
      },
      attachmentWidth: {
        label: 'Width',
        type: 'number',
        min: 0,
        max: 1200,
        step: 40,
        helpText:
          'Specify the width. set zero value to take up the parent width.',
      },
    },
    getDisplayText: (item) => item.props?.attachmentId.value ?? '',
    category: 'Forms',
    icon: { iconName: 'paperclip' },
    canHaveChildren: () => true,
    description: `**Overview**<br/>
The View Attachment component is a versatile document and media viewer designed to render files stored securely within the application's internal file system. Unlike the **Media** component which loads content from external URLs, this component is specifically engineered to fetch and display "Attachments" via their unique system identifiers.
Use this component to embed user-uploaded files—such as PDF documents, images, or video clips—directly into a form or dashboard for review.

**1. File Linking**<br/>
To display a file, you must identify the specific resource using an expression.
* **Attachment Id:** An expression resolving to the unique system identifier (UUID) of the stored file. (Example: \`$.selectedRow.documentId\` or \`$.employee.resumeId\`).
* **Attachment Version:** (Optional) An expression to specify a historical version of the file.
* **Usage:** Use this in document management workflows where you need to display a specific revision (e.g., "v1.2") rather than the current active version.

**2. Supported File Types**<br/>
This component acts as a unified player/viewer for common web-viewable formats:
* **Documents:** PDF Files (renders using the browser's native PDF viewer).
* **Images:** JPG, PNG, GIF, SVG.
* **Media:** MP4 Videos.

**3. Dimensions & Responsiveness**<br/>
You have flexible control over how the viewer fits into your page layout.
* **Height / Width:** Defines the size of the viewer window in pixels. (Constraints: Height 0-800px, Width 0-1200px).
* **Responsive Mode:** Setting either value to **0** instructs the component to fill 100% of the available space in that dimension.
* **Use Case:** Set **Height: 0** and **Width: 0** inside a large modal dialog to make the PDF viewer expand to fill the entire popup.

**4. Interactivity**<br/>
* **TriggerOnClick:** You can attach a **TriggerOnClick** child component.
* **Function:** This turns the viewer area into a clickable element, useful for triggering actions like "Open in New Tab" or "Download File."

**5. Best Use Cases**<br/>
* **Document Review:** Embedding a PDF invoice directly in an approval screen so the manager can read it without downloading.
* **ID Verification:** Displaying a scanned driver's license image next to a user's profile data.
* **Training Portals:** Embedding an instructional MP4 video stored in the system's secure assets.
* **Contract Archives:** Viewing specific historical versions of a signed contract for audit purposes.`,
  };
};

export default ViewAttachmentManager;
